'use client'

import { useState, useEffect } from 'react'
import { Button } from "@/components/ui/button"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import { getDockerComposeFile } from "@/app/actions/docker"
import { FileCode } from "lucide-react"

interface DockerComposeViewerProps {
    configPath: string
}

export function DockerComposeViewer({ configPath }: DockerComposeViewerProps) {
    const [content, setContent] = useState<string>('')
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)
    const [open, setOpen] = useState(false)

    const fetchContent = async () => {
        setLoading(true)
        setError(null)
        try {
            const result = await getDockerComposeFile(configPath)
            if (result.error) {
                setError(result.error)
                setContent('')
            } else {
                setContent(result.content || '')
            }
        } catch (err) {
            console.error("Failed to fetch docker-compose file", err)
            setError("Failed to fetch file.")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (open) {
            fetchContent()
        }
    }, [open])

    // Extract filename from path for display
    const fileName = configPath.split('/').pop() || 'docker-compose.yml'

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                    <FileCode className="mr-2 h-4 w-4" /> Compose
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl h-[80vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle className="pr-8">
                        {fileName}
                    </DialogTitle>
                    <DialogDescription className="pr-8">
                        {configPath}
                    </DialogDescription>
                </DialogHeader>
                <ScrollArea className="flex-1 w-full rounded-md border bg-zinc-900 text-zinc-100 font-mono text-xs overflow-hidden">
                    <div className="p-4 min-h-0">
                        {loading ? (
                            <p className="text-muted-foreground">Loading...</p>
                        ) : error ? (
                            <p className="text-red-400">{error}</p>
                        ) : (
                            <pre className="whitespace-pre-wrap break-all">
                                {content || "No content available."}
                            </pre>
                        )}
                    </div>
                </ScrollArea>
            </DialogContent>
        </Dialog>
    )
}

